<template>
  <div
    class="fe-overflow-hidden"
    :class="[
      {
        'fe-relative': $slots.default,
        'fe-bg-cover fe-bg-center': placeholder !== 'none',
      },
      className,
    ]"
    ref="wrapper"
    :style="{
      backgroundImage: placeholder !== 'none' ? `url(${placeholder})` : null,
    }"
    v-on="$listeners"
    v-bind="$attrs"
  >
    <img
      v-if="showImage"
      :src="src"
      :alt="alt"
      :loading="supportsLazyLoading ? 'lazy' : undefined"
      :class="['fe-w-full fe-h-full', imgClassName]"
    />
    <slot></slot>
  </div>
</template>

<script>
  export default {
    name: "LazyImage",
    props: {
      src: {
        type: String,
        required: true,
      },
      placeholder: {
        type: String,
        default:
          "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMgAAADICAYAAACtWK6eAAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAQdSURBVHgB7dxPS3JNHMfhuSMiKNu0aBW+/3dlixYt2lhBheTzjHAgbry/juePoV0XiLsB5ffhjJ7h/FksFusCbLM+K8A/CQQCgUAgEAgEAoFAIBAIBAKBQCAQCAQCgUAgEAgEAoFAIBAIBAKBQCAQCAQCgUAgEAgEAoFAIBAIBAKBQCAQCAQCgUAgEAgEAoFAIBAIBAKBQCAQCAQCgUAgEAgEAoFAIBAIBAKBQCAQCAQCgUAgEAgEAoFAIBAIBAKBQCAQCAQCgUAgEAgEAoFAIBAIBAKBQCAQCAQCgUAgEAgEAoFAIBAIBAKBQCAQCAQCgUAgEAgEAoFAIBAIBAKB4LzQ7OzsrMxms3J5ebl5HZPPz8+yXC7L29tbod2fxWKxLux0fn5e7u7uNu/HbLValaenp807O61tsRqdQhxVF3q9GrKbb6nB1dXVScTRqZ+lbhXZTSANbm5uyqk5tt9QP0UgDS4uLsqpEUgb/2IN8PDwUI7BfD4v9OMKAoFAIBAIBAKBQCAQCAQCgUAgEAgEAoFAfsDUJ2md1B2Pb/LA6vDW4+a3t7dlClOv/9s4i3VA3fDWw4/dAcjn5+cylqnX/40EciDfh7dzfX29eR9jiNP69GeLdQDbhrdTh3jodmjX+vQnkIml4e0MiaRlffoTyIT2Gd4+kYhjegKZSJ/h3ScScRyGQCYwZHhbIhHH4QhkZC3D+/r6unmQ27+kSMZYn3YCGeDvIW4d3vq3bn14276RjLk+bQQywPch3md4q6+vr70imWJ9dnOjcKDuPsP3u9fbfB/eTjfEafDHWP/+/r7QjyvICOoQ7zu8ndYryZD16U8gE0vD2xmyHWpZn/4EMqF9hrdPJOKYnkAm0md494lEHIchkAkMGd6WSMRxOAIZYNsQjzG8KRJxHJZABvh7iMcc3m2RiOPwBDLA9yGeYninXp/d3CgcqBviqe43TL0+mSvICKYeXnH8HIFAIBAIBAKBQCAQCAQCgUAgEAgEAoFA4KjJAPP5vHDaXEEavL+/l1PjaSdtBNLg4+OjnJqXl5fCbgJpsFwuy2q1KqeifpZ6fJ7dBNKgO3J+CpHUz1A/C238SG9UB+vx8XHzjKrZbHZ0D46uv6PqVrFeDR2fbyeQPdWtie3J72GLBYFAIBAIBAKBQCAQCAQCgUAgEAgEAoFAIBAIBAKBQCAQCAQCgUAgEAgEAoFAIBAIBAKBQCAQCAQCgUAgEAgEAoFAIBAIBAKBQCAQCAQCgUAgEAgEAoFAIBAIBAKBQCAQCAQCgUAgEAgEAoFAIBAIBAKBQCAQCAQCgUAgEAgEAoFAIBAIBAKBQCAQCAQCgUAgEAgEAoFAIBAIBAKBQCAQCAQCgUAgEAgEAoFAIBAIBAKBQCAQCAQCgUBw/v9rXYBt1v8BdASXP6PDFIcAAAAASUVORK5CYII=",
      },
      alt: {
        type: String,
        default: "",
      },
      className: {
        type: [String, Array, Object],
        default: "",
      },
      imgClassName: {
        type: String,
        default: "fe-object-cover",
      },
    },
    data() {
      return {
        supportsLazyLoading: false,
        showImage: false,
      }
    },
    // This mounted method is commented out because when we use browser's inbuilt lazy loading, then even if user has not come to the review and rating section, still few images are being loaded.
    // So for now, we are going ahead with intersection observer approach only
    // mounted() {
    //   this.checkNativeLazyLoading()

    //   if (this.supportsLazyLoading) {
    //     // For browsers supporting native lazy loading, no need for IntersectionObserver
    //     this.showImage = true
    //   } else {
    //     // For browsers that do not support native lazy loading - using Intersection Observer
    //     this.observeImage()
    //   }
    // },
    inheritAttrs: false, // This prevents the component from automatically inheriting attributes to the root element, giving us full control over attribute binding.
    mounted() {
      this.observeImage()
    },
    beforeDestroy() {
      if (this.observer) {
        this.observer.disconnect()
      }
    },
    methods: {
      checkNativeLazyLoading() {
        // Check if the browser supports native lazy loading
        this.supportsLazyLoading = "loading" in HTMLImageElement.prototype
      },
      observeImage() {
        const observerOptions = {
          root: null,
          threshold: 0.1,
        }

        this.observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this.loadImage()
            }
          })
        }, observerOptions)

        this.observer.observe(this.$refs.wrapper)
      },
      loadImage() {
        this.showImage = true
        if (this.observer) {
          this.observer.disconnect() // Disconnect observer once the image is loaded
        }
      },
    },
  }
</script>
